import { useContext, useEffect } from 'react';
import { UserState } from './authUtils';
import { claimsKeys } from './claims';
import useBaseQuery from './baseQuery';
import { ClaimPhotosType } from '../graphql/GetClaimPhotosList';
import { DomainParams } from './graphQLClientUtil';
import NetworkContext from '../contexts/NetworkContext';
import { getClaimPhotos, getPhotosToBeAdded } from '../pages/helper/offlineHelper/ClaimPhotosHelper';
import GET_CLAIM_PHOTOS_LIST_PAGED, { PagedClaimPhotosType } from '../graphql/GetClaimPhotosListPaged';

const useClaimPhotosPaged = (
  region: number,
  claimIndx: number,
  currentPage: number,
  itemsPerPage: number,
  userState: UserState,
  photosList?: ClaimPhotosType[],
  setPhotosList?: (_levelsList: ClaimPhotosType[]) => void,
  setTotalItems?: (totalItems: number) => void,
  onSuccess?: (_data: any, claimIndx: number) => Promise<void>,
) => {
  const network = useContext(NetworkContext);
  const enabled = network.connected && !!claimIndx;

  const onSuccessPhotos = async (_data: { claimPhotosPaged:PagedClaimPhotosType }) => {
    if (_data?.claimPhotosPaged?.items?.length) {
      if (setPhotosList) {
        const newItems = _data?.claimPhotosPaged?.items;
        if (photosList && photosList?.length > 0 && !photosList?.some(p => p.ctfileId === newItems[0].ctfileId)) {
          const updatePhotos = [...photosList, ...newItems];
          setPhotosList(updatePhotos);
        } else if (!photosList || photosList?.length === 0) {
          setPhotosList(newItems);
        }
      }
      if (setTotalItems) {
        setTotalItems(_data?.claimPhotosPaged?.totalItems);
      }
    }
    if (onSuccess) await onSuccess(_data, claimIndx);
  };

  const { data, isLoading, isFetching, refetch } = useBaseQuery(
    region,
    { claimIndx, currentPage, itemsPerPage },
    userState,
    enabled,
    GET_CLAIM_PHOTOS_LIST_PAGED,
    claimsKeys.claimPhotos(),
    DomainParams.Common,
    onSuccessPhotos,
  );

  useEffect(() => {
    if (!network.connected && photosList?.length === 0 && claimIndx) {
      const getAsync = async () => {
        const { photos: localClaimPhotos } = await getClaimPhotos(claimIndx);
        const photosToBeAdded = await getPhotosToBeAdded(claimIndx);
        if ((localClaimPhotos?.length || photosToBeAdded.length) && setPhotosList) {
          setPhotosList([...localClaimPhotos, ...photosToBeAdded]);
        }
      };
      getAsync();
    }
  }, [claimIndx, network.connected, photosList?.length, setPhotosList]);

  const refetchOffline = async () => {
    if (!network.connected && claimIndx) {
      const getAsync = async () => {
        const { photos: localClaimPhotos } = await getClaimPhotos(claimIndx);
        const photosToBeAdded = await getPhotosToBeAdded(claimIndx);
        if ((localClaimPhotos?.length || photosToBeAdded.length) && setPhotosList) {
          setPhotosList([...localClaimPhotos, ...photosToBeAdded]);
        }
      };
      getAsync();
    }
  };
  
  const claimPhotosList: ClaimPhotosType[] =
    !isLoading && !isFetching ? data?.claimPhotosPaged?.items || [] : [];
  return { claimPhotosList, isLoading, isFetching, refetch, refetchOffline };
};

export default useClaimPhotosPaged;